import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'wrapped-redux';
import { useUserSafe } from '@vl-core/hooks/useUser';

export type Article = {
  title: string;
  body: string;
};

const unifyArticle = (article: { title: string; template_body: string }): Article | undefined => {
  if ('template_body' in article) {
    return { title: article.title, body: article.template_body };
  }

  return undefined;
};

const getNoAuthArticle = async (category: string, dispatch: ReturnType<typeof useDispatch>) => {
  const result = await dispatch(actions.fetchArticleNoAuthFromDB(category));
  return unifyArticle(result);
};

const getAuthenticatedArticle = async (category: string, dispatch: ReturnType<typeof useDispatch>) => {
  const article = await dispatch(actions.getArticleNo(category));

  if (!article?.confluence?.length) return undefined;

  const templateCode = article.confluence[0].content_template_code;

  const result = await dispatch(actions.fetchArticleFromDB(templateCode));

  return unifyArticle(result);
};

const commonCategories = [
  'cookies',
  'accessibility',
  'regulatory',
  'instructions_for_use',
  'clinician_instructions',
  'services',
  'restricted_access_services',
  'cookies_essential',
  'cookies_functional',
  'cookies_performance',
  'system_warning'
];

const fetchArticle = async (
  user: {
    user_type_code: string;
  },
  policiesAccepted: boolean,
  category: string,
  dispatch: ReturnType<typeof useDispatch>
) => {
  const isCommonCategory = commonCategories.includes(category);

  try {
    if (!user || isCommonCategory) {
      return await getNoAuthArticle(category, dispatch);
    }

    const { user_type_code } = user;
    const isClinician = user_type_code === 'CLINICIAN';

    if (policiesAccepted || isClinician) {
      return await getAuthenticatedArticle(category, dispatch);
    }

    return undefined;
  } catch {
    return undefined;
  }
};

export default function useArticle(
  category: string,
  policiesData: any = { policiesAccepted: false, policiesLoading: false },
  noAuthFallback = false
): Article | undefined {
  const dispatch = useDispatch();
  const user = useUserSafe();
  const { policiesAccepted, policiesLoading } = policiesData;
  const result = useQuery({
    queryKey: ['useArticle', category],
    queryFn: () => fetchArticle(user || { user_type_code: '' }, policiesAccepted, category, dispatch),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !policiesLoading && (!!user || noAuthFallback)
  });

  return result.data;
}
